import React, { useEffect, useState, Component } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Textarea } from '../components/ui/Textarea';
import { DatePicker } from '../components/ui/DatePicker';
import { Tooltip } from '../components/ui/Tooltip';
import { Pagination } from '../components/ui/Pagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { CalendarView } from '../components/CalendarView';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { useNotifications } from '../contexts/NotificationContext';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { GenerateActivityWizardModal } from '../components/operations/GenerateActivityWizardModal';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Eye, Edit, Trash2, Plus, Save, CheckCircle2, AlertCircle, HardHat, Calendar, FileText, Receipt, GraduationCap, ClipboardList, AlertTriangle, TrendingUp, Wrench, Hammer, Mail, ExternalLink, X, Search, Filter, Download, List, CalendarIcon, LayoutGrid, Euro } from 'lucide-react';
// ─── Section Config ───────────────────────────────────────────────────────────
const sectionConfig: Record<string, {
  title: string;
  description: string;
  icon: React.ElementType;
  newLabel: string;
}> = {
  workers: {
    title: 'Lavoratori',
    description: 'Dipendenti, formazione e sorveglianza sanitaria.',
    icon: HardHat,
    newLabel: 'Nuovo lavoratore'
  },
  deadlines: {
    title: 'Scadenze',
    description: 'Scadenze tecniche e normative.',
    icon: Calendar,
    newLabel: 'Nuova scadenza'
  },
  contracts: {
    title: 'Offerte e contratti',
    description: 'Offerte, contratti attivi e storico.',
    icon: FileText,
    newLabel: 'Nuovo contratto'
  },
  invoices: {
    title: 'Fatturazione',
    description: 'Fatture, proforma e scadenziario.',
    icon: Receipt,
    newLabel: 'Nuova fattura'
  },
  training: {
    title: 'Formazione',
    description: 'Corsi, iscrizioni e certificazioni.',
    icon: GraduationCap,
    newLabel: 'Nuovo corso'
  },
  operations: {
    title: 'Attività operative',
    description: 'Pianificazione e reportistica delle attività.',
    icon: ClipboardList,
    newLabel: 'Nuova attività'
  },
  non_conformities: {
    title: 'Non conformità',
    description: 'NC, reclami e azioni correttive.',
    icon: AlertTriangle,
    newLabel: 'Nuova NC'
  },
  improvements: {
    title: 'Miglioramenti',
    description: 'Proposte e piani di miglioramento.',
    icon: TrendingUp,
    newLabel: 'Nuovo miglioramento'
  },
  equipment: {
    title: 'Macchinari',
    description: 'Asset aziendali e manutenzioni programmate.',
    icon: Wrench,
    newLabel: 'Nuovo macchinario'
  },
  maintenance: {
    title: 'Manutenzione',
    description: 'Storico interventi e manutenzioni.',
    icon: Hammer,
    newLabel: 'Nuova manutenzione'
  }
};
// ─── Columns Config ───────────────────────────────────────────────────────────
const columnsConfigMap: Record<string, {
  key: string;
  label: string;
}[]> = {
  workers: [{
    key: 'name',
    label: 'Lavoratore'
  }, {
    key: 'role',
    label: 'Mansione'
  }, {
    key: 'trainingStatus',
    label: 'Formazione'
  }, {
    key: 'healthStatus',
    label: 'Sorv. sanitaria'
  }, {
    key: 'nextExpiry',
    label: 'Prox. scadenza'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  deadlines: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'description',
    label: 'Descrizione'
  }, {
    key: 'type',
    label: 'Tipologia'
  }, {
    key: 'date',
    label: 'Data scadenza'
  }, {
    key: 'notifyClient',
    label: 'Notifica'
  }, {
    key: 'responsible',
    label: 'Responsabile'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  contracts: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'object',
    label: 'Oggetto'
  }, {
    key: 'type',
    label: 'Tipo'
  }, {
    key: 'value',
    label: 'Valore'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'date',
    label: 'Data'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  invoices: [{
    key: 'number',
    label: 'Numero'
  }, {
    key: 'description',
    label: 'Descrizione'
  }, {
    key: 'amount',
    label: 'Importo'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'dueDate',
    label: 'Scadenza'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  training: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'title',
    label: 'Titolo'
  }, {
    key: 'type',
    label: 'Tipologia'
  }, {
    key: 'mode',
    label: 'Modalità'
  }, {
    key: 'date',
    label: 'Data'
  }, {
    key: 'enrolled',
    label: 'Iscritti'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  operations: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'activityType',
    label: 'Attività'
  }, {
    key: 'ambito',
    label: 'Ambito'
  }, {
    key: 'contract',
    label: 'Contratto'
  }, {
    key: 'operator',
    label: 'Operatore'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'deadline',
    label: 'Scadenza'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  non_conformities: [{
    key: 'number',
    label: 'Codice'
  }, {
    key: 'type',
    label: 'Tipologia'
  }, {
    key: 'origin',
    label: 'Origine'
  }, {
    key: 'description',
    label: 'Descrizione'
  }, {
    key: 'date',
    label: 'Data rilev.'
  }, {
    key: 'responsible',
    label: 'Responsabile'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  improvements: [{
    key: 'number',
    label: 'Codice'
  }, {
    key: 'subject',
    label: 'Oggetto'
  }, {
    key: 'description',
    label: 'Descrizione'
  }, {
    key: 'date',
    label: 'Data proposta'
  }, {
    key: 'responsible',
    label: 'Responsabile'
  }, {
    key: 'status',
    label: 'Stato'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  equipment: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'name',
    label: 'Macchinario'
  }, {
    key: 'type',
    label: 'Tipologia'
  }, {
    key: 'lastMaintenance',
    label: 'Ultima manut.'
  }, {
    key: 'nextMaintenance',
    label: 'Prox. manut.'
  }, {
    key: 'responsible',
    label: 'Responsabile'
  }, {
    key: 'maintenanceType',
    label: 'Tipo manut.'
  }, {
    key: 'actions',
    label: 'Azioni'
  }],
  maintenance: [{
    key: 'code',
    label: 'Codice'
  }, {
    key: 'equipment',
    label: 'Macchinario'
  }, {
    key: 'type',
    label: 'Tipo'
  }, {
    key: 'date',
    label: 'Data esecuzione'
  }, {
    key: 'result',
    label: 'Esito'
  }, {
    key: 'nextDate',
    label: 'Prox. scadenza'
  }, {
    key: 'responsible',
    label: 'Responsabile'
  }, {
    key: 'actions',
    label: 'Azioni'
  }]
};
// ─── Mock Data ────────────────────────────────────────────────────────────────
const mockWorkers = [{
  id: 1,
  name: 'Mario Rossi',
  role: 'Operaio',
  trainingStatus: 'valid',
  healthStatus: 'fit',
  nextExpiry: '12/05/2025'
}, {
  id: 2,
  name: 'Luigi Verdi',
  role: 'Impiegato',
  trainingStatus: 'expiring',
  healthStatus: 'fit',
  nextExpiry: '10/06/2025'
}, {
  id: 3,
  name: 'Anna Bianchi',
  role: 'Tecnico',
  trainingStatus: 'expired',
  healthStatus: 'fit',
  nextExpiry: '15/01/2025'
}, {
  id: 4,
  name: 'Carlo Neri',
  role: 'Magazziniere',
  trainingStatus: 'valid',
  healthStatus: 'fit',
  nextExpiry: '20/08/2025'
}, {
  id: 5,
  name: 'Giulia Russo',
  role: 'Amministrativa',
  trainingStatus: 'valid',
  healthStatus: 'fit',
  nextExpiry: '05/09/2025'
}, {
  id: 6,
  name: 'Fabio Conti',
  role: 'Preposto',
  trainingStatus: 'expiring',
  healthStatus: 'fit',
  nextExpiry: '01/07/2025'
}, {
  id: 7,
  name: 'Sara Mancini',
  role: 'RLS',
  trainingStatus: 'valid',
  healthStatus: 'fit',
  nextExpiry: '18/10/2025'
}];
const mockDeadlines = [{
  id: 1,
  code: 'SCA-2025/001',
  description: 'Rinnovo DVR',
  type: 'Audit interno',
  date: '2025-06-15',
  notifyClient: 'Sì',
  responsible: 'Alessio',
  status: 'In scadenza'
}, {
  id: 2,
  code: 'SCA-2025/002',
  description: 'Verifica estintori',
  type: 'Prova di emergenza',
  date: '2025-06-20',
  notifyClient: 'No',
  responsible: 'Lorenzo',
  status: 'Pianificata'
}, {
  id: 3,
  code: 'SCA-2025/003',
  description: 'Tampone acque',
  type: 'Tampone alimentare',
  date: '2025-07-01',
  notifyClient: 'Sì',
  responsible: 'Gian Franco',
  status: 'Pianificata'
}, {
  id: 4,
  code: 'SCA-2025/004',
  description: 'Riunione sicurezza',
  type: 'Riunione annuale',
  date: '2025-05-30',
  notifyClient: 'Sì',
  responsible: 'Alessio',
  status: 'Urgente'
}, {
  id: 5,
  code: 'SCA-2025/005',
  description: 'Campionamento prodotti',
  type: 'Campionamento alimentare',
  date: '2025-08-10',
  notifyClient: 'No',
  responsible: 'Tiziano',
  status: 'Pianificata'
}, {
  id: 6,
  code: 'SCA-2025/006',
  description: 'Report annuale sicurezza',
  type: 'Report annuale',
  date: '2025-12-31',
  notifyClient: 'Sì',
  responsible: 'Alessio',
  status: 'Pianificata'
}, {
  id: 7,
  code: 'SCA-2025/007',
  description: 'Corso aggiornamento RLS',
  type: 'Audit interno',
  date: '2025-05-28',
  notifyClient: 'No',
  responsible: 'Lorenzo',
  status: 'Urgente'
}];
const mockContracts = [{
  id: 1,
  code: 'CTR-2025/001',
  object: 'Sicurezza Annuale',
  type: 'Contratto',
  value: '€ 3.200',
  status: 'Attivo',
  date: '01/01/2025'
}, {
  id: 2,
  code: 'CTR-2025/002',
  object: 'HACCP Base',
  type: 'Contratto',
  value: '€ 1.500',
  status: 'Attivo',
  date: '15/02/2025'
}, {
  id: 3,
  code: 'PREV-2025/001',
  object: 'Formazione Lavoratori',
  type: 'Offerta',
  value: '€ 2.800',
  status: 'In attesa',
  date: '10/05/2025'
}, {
  id: 4,
  code: 'CTR-2024/008',
  object: 'Privacy GDPR',
  type: 'Contratto',
  value: '€ 900',
  status: 'Scaduto',
  date: '01/01/2024'
}, {
  id: 5,
  code: 'PREV-2025/002',
  object: 'Audit Interno',
  type: 'Offerta',
  value: '€ 1.200',
  status: 'Bozza',
  date: '20/05/2025'
}, {
  id: 6,
  code: 'CTR-2025/003',
  object: 'Sorveglianza Sanitaria',
  type: 'Contratto',
  value: '€ 2.100',
  status: 'Attivo',
  date: '01/03/2025'
}, {
  id: 7,
  code: 'PREV-2025/003',
  object: 'Aggiornamento DVR',
  type: 'Offerta',
  value: '€ 750',
  status: 'In attesa',
  date: '12/05/2025'
}];
const mockInvoices = [{
  id: 1,
  number: 'FT-2025/045',
  description: 'Canone sicurezza Q1',
  amount: '€ 800',
  status: 'Pagata',
  dueDate: '15/04/2025'
}, {
  id: 2,
  number: 'FT-2025/052',
  description: 'Corso formazione HACCP',
  amount: '€ 1.200',
  status: 'In attesa',
  dueDate: '10/05/2025'
}, {
  id: 3,
  number: 'PRF-2025/012',
  description: 'Consulenza privacy',
  amount: '€ 450',
  status: 'Bozza',
  dueDate: '20/05/2025'
}, {
  id: 4,
  number: 'FT-2025/038',
  description: 'Sopralluogo periodico',
  amount: '€ 350',
  status: 'Pagata',
  dueDate: '01/04/2025'
}, {
  id: 5,
  number: 'FT-2025/061',
  description: 'Aggiornamento DVR',
  amount: '€ 750',
  status: 'Scaduta',
  dueDate: '30/04/2025'
}, {
  id: 6,
  number: 'FT-2025/067',
  description: 'Sorveglianza sanitaria',
  amount: '€ 520',
  status: 'In attesa',
  dueDate: '15/06/2025'
}, {
  id: 7,
  number: 'PRF-2025/015',
  description: 'Audit interno',
  amount: '€ 600',
  status: 'Bozza',
  dueDate: '30/06/2025'
}];
const mockTraining = [{
  id: 1,
  code: 'CRS-001',
  title: 'Sicurezza Generale Lavoratori',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2025-05-20',
  enrolled: 5,
  status: 'In corso'
}, {
  id: 2,
  code: 'CRS-002',
  title: 'HACCP Responsabile',
  type: 'HACCP',
  mode: 'Sede cliente',
  date: '2025-05-22',
  enrolled: 3,
  status: 'In corso'
}, {
  id: 3,
  code: 'CRS-003',
  title: 'Antincendio Rischio Medio',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2025-06-05',
  enrolled: 4,
  status: 'Programmato'
}, {
  id: 4,
  code: 'CRS-004',
  title: 'Primo Soccorso Gruppo B',
  type: 'Sicurezza',
  mode: 'A distanza',
  date: '2025-06-10',
  enrolled: 6,
  status: 'Programmato'
}, {
  id: 5,
  code: 'CRS-005',
  title: 'Aggiornamento RLS',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2025-04-15',
  enrolled: 2,
  status: 'Completato'
}, {
  id: 6,
  code: 'CRS-006',
  title: 'Preposto Aggiornamento',
  type: 'Sicurezza',
  mode: 'Sede SEF',
  date: '2025-03-10',
  enrolled: 3,
  status: 'Completato'
}, {
  id: 7,
  code: 'CRS-007',
  title: 'Privacy GDPR Base',
  type: 'Privacy',
  mode: 'A distanza',
  date: '2025-07-01',
  enrolled: 0,
  status: 'Programmato'
}];
const mockOperations = [{
  id: 1,
  code: 'ATT-2025/001',
  activityType: 'Sopralluogo periodico',
  ambito: 'Sicurezza',
  contract: 'CTR-2025/001',
  operator: 'Alessio',
  zone: 'Arezzo',
  status: 'completo',
  deadline: '30/04/2025'
}, {
  id: 2,
  code: 'ATT-2025/002',
  activityType: 'Backoffice – Redazione documentazione',
  ambito: 'Privacy',
  contract: 'CTR-2025/002',
  operator: 'Lorenzo',
  zone: 'Arezzo',
  status: 'in_svolgimento_attivo',
  deadline: '15/06/2025'
}, {
  id: 3,
  code: 'ATT-2025/003',
  activityType: 'Sopralluogo iniziale e check list',
  ambito: 'HACCP',
  contract: 'CTR-2025/002',
  operator: 'Gian Franco',
  zone: 'Arezzo',
  status: 'calendarizzato',
  deadline: '20/06/2025'
}, {
  id: 4,
  code: 'ATT-2025/004',
  activityType: 'Campionamenti e tamponi alimentari',
  ambito: 'HACCP',
  contract: 'CTR-2025/002',
  operator: 'Alessio',
  zone: 'Arezzo',
  status: 'pianificato',
  deadline: '30/07/2025'
}, {
  id: 5,
  code: 'ATT-2025/005',
  activityType: 'Riunione in ufficio',
  ambito: 'Sicurezza',
  contract: 'CTR-2025/001',
  operator: 'Lorenzo',
  zone: 'Arezzo',
  status: 'da_pianificare',
  deadline: '31/08/2025'
}, {
  id: 6,
  code: 'ATT-2025/006',
  activityType: 'Consulenza via email',
  ambito: 'Ambiente',
  contract: 'CTR-2025/003',
  operator: 'Tiziano',
  zone: 'Arezzo',
  status: 'completo',
  deadline: '10/04/2025'
}, {
  id: 7,
  code: 'ATT-2025/007',
  activityType: 'Sopralluogo consegna',
  ambito: 'Sicurezza',
  contract: 'CTR-2025/001',
  operator: 'Alessio',
  zone: 'Arezzo',
  status: 'in_svolgimento_sospeso',
  deadline: '25/06/2025'
}];
const mockNonConformities = [{
  id: 1,
  number: 'NC-2025/001',
  type: 'Non conformità maggiore',
  origin: 'Interna',
  description: 'Mancanza DPI in reparto produzione',
  date: '2025-04-12',
  responsible: 'Lorenzo',
  status: 'Aperta'
}, {
  id: 2,
  number: 'NC-2025/002',
  type: 'Non conformità minore',
  origin: 'Cliente (reclamo)',
  description: 'Documentazione incompleta',
  date: '2025-05-05',
  responsible: 'Alessio',
  status: 'Chiusa'
}, {
  id: 3,
  number: 'NC-2025/003',
  type: 'Osservazione',
  origin: 'Interna',
  description: 'Segnaletica non aggiornata',
  date: '2025-05-15',
  responsible: 'Gian Franco',
  status: 'Aperta'
}, {
  id: 4,
  number: 'NC-2025/004',
  type: 'Non conformità minore',
  origin: 'Fornitore',
  description: 'Prodotto non conforme alle specifiche',
  date: '2025-03-20',
  responsible: 'Lorenzo',
  status: 'Chiusa'
}, {
  id: 5,
  number: 'NC-2025/005',
  type: 'Osservazione',
  origin: 'Interna',
  description: 'Procedura non rispettata',
  date: '2025-05-22',
  responsible: 'Alessio',
  status: 'Sospesa'
}, {
  id: 6,
  number: 'NC-2025/006',
  type: 'Non conformità maggiore',
  origin: 'Cliente (reclamo)',
  description: 'Ritardo nella consegna documentazione',
  date: '2025-04-30',
  responsible: 'Tiziano',
  status: 'Aperta'
}];
const mockImprovements = [{
  id: 1,
  number: 'MIG-2025/001',
  subject: 'Digitalizzazione archivio DVR',
  description: 'Proposta per archiviazione digitale di tutti i DVR',
  date: '2025-03-01',
  responsible: 'Alessio',
  status: 'Approvato'
}, {
  id: 2,
  number: 'MIG-2025/002',
  subject: 'Formazione extra sicurezza',
  description: 'Corso aggiuntivo per addetti al magazzino',
  date: '2025-04-15',
  responsible: 'Lorenzo',
  status: 'In valutazione'
}, {
  id: 3,
  number: 'MIG-2025/003',
  subject: 'Nuovi DPI ergonomici',
  description: 'Sostituzione DPI con modelli certificati ergonomici',
  date: '2025-01-10',
  responsible: 'Gian Franco',
  status: 'Approvato'
}, {
  id: 4,
  number: 'MIG-2025/004',
  subject: 'Aggiornamento piano emergenza',
  description: 'Revisione completa del piano di emergenza',
  date: '2025-05-01',
  responsible: 'Alessio',
  status: 'In valutazione'
}, {
  id: 5,
  number: 'MIG-2025/005',
  subject: 'Segnaletica bilingue',
  description: 'Aggiunta segnaletica in italiano e inglese',
  date: '2025-02-20',
  responsible: 'Tiziano',
  status: 'Sospesa'
}, {
  id: 6,
  number: 'MIG-2025/006',
  subject: 'Check-list digitale sopralluoghi',
  description: 'App mobile per compilazione check-list in loco',
  date: '2025-05-10',
  responsible: 'Lorenzo',
  status: 'In valutazione'
}];
const mockEquipment = [{
  id: 1,
  code: 'EQP-001',
  name: 'Fonometro Brüel & Kjær',
  type: 'Strumento Misura',
  lastMaintenance: '2025-01-10',
  nextMaintenance: '2026-01-10',
  responsible: 'Alessio',
  maintenanceType: 'Ordinaria'
}, {
  id: 2,
  code: 'EQP-002',
  name: 'Carrello elevatore Toyota',
  type: 'Veicolo',
  lastMaintenance: '2025-03-15',
  nextMaintenance: '2025-09-15',
  responsible: 'Lorenzo',
  maintenanceType: 'Ordinaria'
}, {
  id: 3,
  code: 'EQP-003',
  name: 'Pressa idraulica 50T',
  type: 'Attrezzatura',
  lastMaintenance: '2024-12-01',
  nextMaintenance: '2025-06-01',
  responsible: 'Gian Franco',
  maintenanceType: 'Straordinaria'
}, {
  id: 4,
  code: 'EQP-004',
  name: 'Notebook Dell Latitude',
  type: 'Hardware',
  lastMaintenance: '2025-02-20',
  nextMaintenance: '2026-02-20',
  responsible: 'Alessio',
  maintenanceType: 'Ordinaria'
}, {
  id: 5,
  code: 'EQP-005',
  name: 'Trapano a colonna',
  type: 'Attrezzatura',
  lastMaintenance: '2025-04-05',
  nextMaintenance: '2025-10-05',
  responsible: 'Lorenzo',
  maintenanceType: 'Ordinaria'
}, {
  id: 6,
  code: 'EQP-006',
  name: 'Misuratore CO2',
  type: 'Strumento Misura',
  lastMaintenance: '2025-01-25',
  nextMaintenance: '2026-01-25',
  responsible: 'Tiziano',
  maintenanceType: 'Ordinaria'
}, {
  id: 7,
  code: 'EQP-007',
  name: 'Autocarro Iveco Daily',
  type: 'Veicolo',
  lastMaintenance: '2025-04-01',
  nextMaintenance: '2025-10-01',
  responsible: 'Gian Franco',
  maintenanceType: 'Straordinaria'
}];
const mockMaintenance = [{
  id: 1,
  code: 'MNT-2025/001',
  equipment: 'Fonometro Brüel & Kjær',
  type: 'Ordinaria',
  date: '2025-01-10',
  result: 'Positivo',
  nextDate: '2026-01-10',
  responsible: 'Alessio'
}, {
  id: 2,
  code: 'MNT-2025/002',
  equipment: 'Carrello elevatore Toyota',
  type: 'Ordinaria',
  date: '2025-03-15',
  result: 'Positivo',
  nextDate: '2025-09-15',
  responsible: 'Lorenzo'
}, {
  id: 3,
  code: 'MNT-2025/003',
  equipment: 'Pressa idraulica 50T',
  type: 'Straordinaria',
  date: '2024-12-01',
  result: 'Negativo',
  nextDate: '2025-06-01',
  responsible: 'Gian Franco'
}, {
  id: 4,
  code: 'MNT-2025/004',
  equipment: 'Notebook Dell Latitude',
  type: 'Ordinaria',
  date: '2025-02-20',
  result: 'Positivo',
  nextDate: '2026-02-20',
  responsible: 'Alessio'
}, {
  id: 5,
  code: 'MNT-2025/005',
  equipment: 'Trapano a colonna',
  type: 'Ordinaria',
  date: '2025-04-05',
  result: 'Positivo',
  nextDate: '2025-10-05',
  responsible: 'Lorenzo'
}, {
  id: 6,
  code: 'MNT-2025/006',
  equipment: 'Misuratore CO2',
  type: 'Ordinaria',
  date: '2025-01-25',
  result: 'Positivo',
  nextDate: '2026-01-25',
  responsible: 'Tiziano'
}, {
  id: 7,
  code: 'MNT-2025/007',
  equipment: 'Autocarro Iveco Daily',
  type: 'Straordinaria',
  date: '2025-04-01',
  result: 'Positivo',
  nextDate: '2025-10-01',
  responsible: 'Gian Franco'
}];
// ─── Helpers ──────────────────────────────────────────────────────────────────
function getSectionMockData(sectionId: string): any[] {
  switch (sectionId) {
    case 'workers':
      return mockWorkers;
    case 'deadlines':
      return mockDeadlines;
    case 'contracts':
      return mockContracts;
    case 'invoices':
      return mockInvoices;
    case 'training':
      return mockTraining;
    case 'operations':
      return mockOperations;
    case 'non_conformities':
      return mockNonConformities;
    case 'improvements':
      return mockImprovements;
    case 'equipment':
      return mockEquipment;
    case 'maintenance':
      return mockMaintenance;
    default:
      return [];
  }
}
function getSectionSearchFields(sectionId: string): string[] {
  switch (sectionId) {
    case 'workers':
      return ['name', 'role'];
    case 'deadlines':
      return ['description', 'type', 'code'];
    case 'contracts':
      return ['code', 'object'];
    case 'invoices':
      return ['number', 'description'];
    case 'training':
      return ['title', 'code', 'type'];
    case 'operations':
      return ['activityType', 'ambito', 'code'];
    case 'non_conformities':
      return ['number', 'description', 'origin'];
    case 'improvements':
      return ['number', 'subject', 'description'];
    case 'equipment':
      return ['code', 'name', 'type'];
    case 'maintenance':
      return ['code', 'equipment'];
    default:
      return [];
  }
}
function getDefaultColumns(sectionId: string): string[] {
  return (columnsConfigMap[sectionId] || []).map(c => c.key);
}
function getLockedColumns(sectionId: string): string[] {
  return sectionId === 'workers' ? ['name'] : ['actions'];
}
const statusColors: Record<string, string> = {
  da_pianificare: 'neutral',
  pianificato: 'info',
  calendarizzato: 'primary',
  in_svolgimento_attivo: 'success',
  in_svolgimento_sospeso: 'warning',
  in_svolgimento_stop: 'danger',
  completo: 'success'
};
const statusLabels: Record<string, string> = {
  da_pianificare: 'Da pianificare',
  pianificato: 'Pianificata',
  calendarizzato: 'Calendarizzata',
  in_svolgimento_attivo: 'Attiva',
  in_svolgimento_sospeso: 'Sospesa',
  in_svolgimento_stop: 'Stop',
  completo: 'Completa'
};
// ─── Shared Delete Modal ──────────────────────────────────────────────────────
function DeleteModal({
  isOpen,
  onClose,
  onConfirm,
  itemLabel,
  deleteSuccess
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemLabel: string;
  deleteSuccess: boolean;
}) {
  return <Modal isOpen={isOpen} onClose={onClose} title="Conferma eliminazione" size="sm" data-id="element-1495">
      <AnimatePresence mode="wait" data-id="element-1496">
        {deleteSuccess ? <motion.div key="success" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="flex flex-col items-center justify-center py-12" data-id="element-1497">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 150,
          damping: 15,
          delay: 0.1
        }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-1498">
              <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-1499" />
            </motion.div>
            <p className="text-lg font-semibold text-slate-800" data-id="element-1500">
              Eliminato con successo
            </p>
          </motion.div> : <motion.div key="confirm" initial={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-1501">
            <div className="space-y-6" data-id="element-1502">
              <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-1503">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-1504" />
                <div data-id="element-1505">
                  <p className="text-sm text-red-700 font-medium" data-id="element-1506">
                    Attenzione: questa azione è irreversibile.
                  </p>
                  <p className="text-sm text-red-600 mt-1" data-id="element-1507">
                    L'elemento <strong data-id="element-1508">{itemLabel}</strong> verrà eliminato
                    definitivamente.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4" data-id="element-1509">
                <Button variant="flat" color="default" onClick={onClose} data-id="element-1510">
                  Annulla
                </Button>
                <Button color="danger" onClick={onConfirm} startContent={<Trash2 className="h-4 w-4" data-id="element-1512" />} data-id="element-1511">
                  Elimina
                </Button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </Modal>;
}
// ─── Save Success Overlay ─────────────────────────────────────────────────────
function SaveSuccessView({
  label
}: {
  label: string;
}) {
  return <motion.div key="success" initial={{
    opacity: 0,
    scale: 0.8
  }} animate={{
    opacity: 1,
    scale: 1
  }} className="flex flex-col items-center justify-center py-16" data-id="element-1513">
      <motion.div initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      type: 'spring',
      stiffness: 200,
      damping: 15
    }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-1514">
        <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-1515" />
      </motion.div>
      <p className="text-lg font-semibold text-slate-800" data-id="element-1516">{label}</p>
    </motion.div>;
}
// ─── Props ────────────────────────────────────────────────────────────────────
interface ClientSectionPageProps {
  sectionId: string;
  clientName: string;
  onBack: () => void;
}
// ─── Main Component ───────────────────────────────────────────────────────────
export function ClientSectionPage({
  sectionId,
  clientName,
  onBack
}: ClientSectionPageProps) {
  const {
    addNotification
  } = useNotifications();
  const config = sectionConfig[sectionId] || sectionConfig['workers'];
  const Icon = config.icon;
  // ─── Shared modal state ───────────────────────────────────────────────────
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isGenerateActivityOpen, setIsGenerateActivityOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  // ─── Toolbar state ────────────────────────────────────────────────────────
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(getDefaultColumns(sectionId));
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  // ─── Hooks (called unconditionally at top level) ──────────────────────────
  const sectionData = getSectionMockData(sectionId);
  const searchFields = getSectionSearchFields(sectionId);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(sectionData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, searchFields);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  // Reset toolbar state when section changes
  useEffect(() => {
    clearFilters();
    setSearchTerm('');
    setShowFilters(false);
    setViewMode('list');
    setVisibleColumns(getDefaultColumns(sectionId));
    setCurrentPage(1);
  }, [sectionId]);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData({
      ...item
    });
    setIsEditOpen(true);
  };
  const handleDelete = (item: any) => {
    setSelectedItem(item);
    setIsDeleteOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({});
    setIsEditOpen(true);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsEditOpen(false);
      addNotification('success', 'Salvato', 'Elemento salvato con successo.');
    }, 1500);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setIsDeleteOpen(false);
      addNotification('success', 'Eliminato', 'Elemento eliminato con successo.');
    }, 1500);
  };
  const activeFilterCount = Object.keys(filters).filter(k => (filters as any)[k]).length;
  // ─── Shared Toolbar ───────────────────────────────────────────────────────
  const cols = columnsConfigMap[sectionId] || [];
  const toolbar = <Card className="mb-6" noPadding data-id="element-1517">
      <div className="p-4 flex flex-col gap-4" data-id="element-1518">
        <div className="flex flex-col sm:flex-row gap-4" data-id="element-1519">
          <div className="flex-1" data-id="element-1520">
            <Input placeholder={`Cerca in ${config.title.toLowerCase()}...`} startContent={<Search className="h-4 w-4" data-id="element-1522" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-1521" />
          </div>
          <div className="flex gap-2" data-id="element-1523">
            <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-1525" />} data-id="element-1524">
              Filtri
              {activeFilterCount > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-1526">
                  {activeFilterCount}
                </Badge>}
            </Button>
            <ColumnCustomizer columns={cols} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey={`client-${sectionId}`} lockedColumns={getLockedColumns(sectionId)} data-id="element-1527" />
            <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-1529" />} data-id="element-1528">
              Esporta
            </Button>
          </div>
        </div>

        {showFilters && <div className="pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-1530">
            <SectionFilters sectionId={sectionId} filters={filters} setFilter={setFilter} data-id="element-1531" />
            <div className="flex justify-end mt-4" data-id="element-1532">
              <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-1534" />} data-id="element-1533">
                Resetta filtri
              </Button>
            </div>
          </div>}
      </div>
    </Card>;
  // ─── Page Header ──────────────────────────────────────────────────────────
  const header = <>
      <div className="mb-6" data-id="element-1535">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-seform-navy transition-colors group" data-id="element-1536">
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" data-id="element-1537" />
          <span data-id="element-1538">{clientName}</span>
        </button>
      </div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-1539">
        <div data-id="element-1540">
          <div className="flex items-center gap-3 mb-1" data-id="element-1541">
            <Icon className="h-6 w-6 text-seform-navy" data-id="element-1542" />
            <h1 className="text-3xl font-bold text-seform-navy" data-id="element-1543">
              {config.title}
            </h1>
          </div>
          <p className="text-slate-500 mt-1" data-id="element-1544">{config.description}</p>
        </div>
        <div className="flex items-center gap-3" data-id="element-1545">
          {sectionId === 'deadlines' && <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-1546">
              <button onClick={() => setViewMode('calendar')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'calendar' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1547">
                <CalendarIcon className="h-4 w-4 inline-block mr-1.5" data-id="element-1548" />{' '}
                Calendario
              </button>
              <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1549">
                <List className="h-4 w-4 inline-block mr-1.5" data-id="element-1550" /> Lista
              </button>
            </div>}
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-1552" />} data-id="element-1551">
            {config.newLabel}
          </Button>
        </div>
      </div>
    </>;
  // ─── Action Buttons (shared) ──────────────────────────────────────────────
  const actionButtons = (item: any, label: string) => <div className="flex items-center gap-1" data-id="element-1553">
      <Tooltip content={`Visualizza ${label}`} data-id="element-1554">
        <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-1555">
          <Eye className="h-4 w-4 text-slate-500" data-id="element-1556" />
        </Button>
      </Tooltip>
      <Tooltip content={`Modifica ${label}`} data-id="element-1557">
        <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-1558">
          <Edit className="h-4 w-4 text-seform-orange" data-id="element-1559" />
        </Button>
      </Tooltip>
      <Tooltip content={`Elimina ${label}`} data-id="element-1560">
        <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-1561">
          <Trash2 className="h-4 w-4 text-red-500" data-id="element-1562" />
        </Button>
      </Tooltip>
    </div>;
  // ─── Workers Section ──────────────────────────────────────────────────────
  if (sectionId === 'workers') {
    return <Layout data-id="element-1563">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-1564">
          <div className="overflow-x-auto" data-id="element-1565">
            <table className="w-full text-left border-collapse table-auto" data-id="element-1566">
              <thead data-id="element-1567">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1568">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-1569">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1570">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1571">
                    {visibleColumns.includes('name') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1572">
                        {item.name}
                      </td>}
                    {visibleColumns.includes('role') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1573">
                        {item.role}
                      </td>}
                    {visibleColumns.includes('trainingStatus') && <td className="px-4 py-4" data-id="element-1574">
                        <Badge variant={item.trainingStatus === 'valid' ? 'success' : item.trainingStatus === 'expiring' ? 'warning' : 'danger'} data-id="element-1575">
                          {item.trainingStatus === 'valid' ? 'In regola' : item.trainingStatus === 'expiring' ? 'In scadenza' : 'Scaduto'}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('healthStatus') && <td className="px-4 py-4" data-id="element-1576">
                        <Badge variant="success" data-id="element-1577">Idoneo</Badge>
                      </td>}
                    {visibleColumns.includes('nextExpiry') && <td className="px-4 py-4 text-sm font-mono text-slate-600" data-id="element-1578">
                        {item.nextExpiry}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-1579">
                        {actionButtons(item, 'lavoratore')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-1580">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-1581" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Scheda lavoratore" size="lg" data-id="element-1582">
          {selectedItem && <div className="space-y-6" data-id="element-1583">
              <div className="grid grid-cols-2 gap-4" data-id="element-1584">
                <div data-id="element-1585">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1586">
                    Nome
                  </span>
                  <p className="font-medium mt-1" data-id="element-1587">{selectedItem.name}</p>
                </div>
                <div data-id="element-1588">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1589">
                    Mansione
                  </span>
                  <p className="font-medium mt-1" data-id="element-1590">{selectedItem.role}</p>
                </div>
                <div data-id="element-1591">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1592">
                    Formazione
                  </span>
                  <div className="mt-1" data-id="element-1593">
                    <Badge variant={selectedItem.trainingStatus === 'valid' ? 'success' : selectedItem.trainingStatus === 'expiring' ? 'warning' : 'danger'} data-id="element-1594">
                      {selectedItem.trainingStatus === 'valid' ? 'In regola' : selectedItem.trainingStatus === 'expiring' ? 'In scadenza' : 'Scaduto'}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-1595">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1596">
                    Sorveglianza sanitaria
                  </span>
                  <div className="mt-1" data-id="element-1597">
                    <Badge variant="success" data-id="element-1598">Idoneo</Badge>
                  </div>
                </div>
                <div data-id="element-1599">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1600">
                    Prossima scadenza
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-1601">
                    {selectedItem.nextExpiry}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1602">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-1603">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-1605" />} data-id="element-1604">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica lavoratore' : 'Nuovo lavoratore'} size="lg" data-id="element-1606">
          <AnimatePresence mode="wait" data-id="element-1607">
            {saveSuccess ? <SaveSuccessView label="Lavoratore salvato con successo" data-id="element-1608" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-1609">
                <div className="space-y-6" data-id="element-1610">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1611">
                    <Input label="Nome e cognome" value={formData.name || ''} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required data-id="element-1612" />
                    <Input label="Mansione" value={formData.role || ''} onChange={e => setFormData({
                  ...formData,
                  role: e.target.value
                })} data-id="element-1613" />
                    <Select label="Stato formazione" value={formData.trainingStatus || ''} onChange={e => setFormData({
                  ...formData,
                  trainingStatus: e.target.value
                })} options={[{
                  value: 'valid',
                  label: 'In regola'
                }, {
                  value: 'expiring',
                  label: 'In scadenza'
                }, {
                  value: 'expired',
                  label: 'Scaduto'
                }]} data-id="element-1614" />
                    <Input label="Prossima scadenza" type="date" value={formData.nextExpiry || ''} onChange={e => setFormData({
                  ...formData,
                  nextExpiry: e.target.value
                })} data-id="element-1615" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1616">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-1617">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-1619" />} data-id="element-1618">
                      Salva lavoratore
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.name || ''} deleteSuccess={deleteSuccess} data-id="element-1620" />
      </Layout>;
  }
  // ─── Deadlines Section ────────────────────────────────────────────────────
  if (sectionId === 'deadlines') {
    return <Layout data-id="element-1621">
        {header}
        {viewMode === 'list' ? <>
            {toolbar}
            <Card noPadding className="overflow-hidden" data-id="element-1622">
              <div className="overflow-x-auto" data-id="element-1623">
                <table className="w-full text-left border-collapse table-auto" data-id="element-1624">
                  <thead data-id="element-1625">
                    <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1626">
                      {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-1627">
                            {col.label}
                          </th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-1628">
                    {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1629">
                        {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-1630">
                            {item.code}
                          </td>}
                        {visibleColumns.includes('description') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1631">
                            {item.description}
                          </td>}
                        {visibleColumns.includes('type') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1632">
                            {item.type}
                          </td>}
                        {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm font-mono text-slate-600" data-id="element-1633">
                            {item.date}
                          </td>}
                        {visibleColumns.includes('notifyClient') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1634">
                            {item.notifyClient}
                          </td>}
                        {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1635">
                            {item.responsible}
                          </td>}
                        {visibleColumns.includes('status') && <td className="px-4 py-4 whitespace-nowrap" data-id="element-1636">
                            <Badge variant={item.status === 'Urgente' ? 'danger' : item.status === 'In scadenza' ? 'warning' : item.status === 'Completata' ? 'success' : 'info'} data-id="element-1637">
                              {item.status}
                            </Badge>
                          </td>}
                        {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-1638">
                            {actionButtons(item, 'scadenza')}
                          </td>}
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-slate-100" data-id="element-1639">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-1640" />
              </div>
            </Card>
          </> : <CalendarView events={searchResult.map((item: any) => ({
        id: String(item.id),
        title: item.description,
        date: item.date,
        type: item.type,
        priority: item.status === 'Urgente' ? 'high' : item.status === 'In scadenza' ? 'medium' : 'low',
        client: clientName
      }))} onEventClick={(event: any) => {
        const found = mockDeadlines.find(d => String(d.id) === event.id);
        if (found) handleView(found);
      }} data-id="element-1641" />}

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Dettaglio scadenza" size="lg" data-id="element-1642">
          {selectedItem && <div className="space-y-6" data-id="element-1643">
              <div className="grid grid-cols-2 gap-4" data-id="element-1644">
                <div data-id="element-1645">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1646">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-1647">
                    {selectedItem.code}
                  </p>
                </div>
                <div data-id="element-1648">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1649">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-1650">
                    <Badge variant={selectedItem.status === 'Urgente' ? 'danger' : selectedItem.status === 'In scadenza' ? 'warning' : 'info'} data-id="element-1651">
                      {selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-1652">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1653">
                    Descrizione
                  </span>
                  <p className="font-medium mt-1" data-id="element-1654">{selectedItem.description}</p>
                </div>
                <div data-id="element-1655">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1656">
                    Tipologia
                  </span>
                  <p className="font-medium mt-1" data-id="element-1657">{selectedItem.type}</p>
                </div>
                <div data-id="element-1658">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1659">
                    Data scadenza
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-1660">
                    {selectedItem.date}
                  </p>
                </div>
                <div data-id="element-1661">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1662">
                    Responsabile
                  </span>
                  <p className="font-medium mt-1" data-id="element-1663">{selectedItem.responsible}</p>
                </div>
                <div data-id="element-1664">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1665">
                    Notifica cliente
                  </span>
                  <p className="font-medium mt-1" data-id="element-1666">
                    {selectedItem.notifyClient}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1667">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-1668">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-1670" />} data-id="element-1669">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica scadenza' : 'Nuova scadenza'} size="lg" data-id="element-1671">
          <AnimatePresence mode="wait" data-id="element-1672">
            {saveSuccess ? <SaveSuccessView label="Scadenza salvata con successo" data-id="element-1673" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-1674">
                <div className="space-y-6" data-id="element-1675">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1676">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} data-id="element-1677" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Riunione annuale',
                  label: 'Riunione annuale'
                }, {
                  value: 'Prova di emergenza',
                  label: 'Prova di emergenza'
                }, {
                  value: 'Tampone alimentare',
                  label: 'Tampone alimentare'
                }, {
                  value: 'Campionamento alimentare',
                  label: 'Campionamento alimentare'
                }, {
                  value: 'Audit interno',
                  label: 'Audit interno'
                }, {
                  value: 'Report annuale',
                  label: 'Report annuale'
                }]} required data-id="element-1678" />
                    <div className="col-span-full" data-id="element-1679">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} required data-id="element-1680" />
                    </div>
                    <DatePicker label="Data scadenza" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-1681" />
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-1682" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Pianificata',
                  label: 'Pianificata'
                }, {
                  value: 'In scadenza',
                  label: 'In scadenza'
                }, {
                  value: 'Urgente',
                  label: 'Urgente'
                }, {
                  value: 'Completata',
                  label: 'Completata'
                }]} required data-id="element-1683" />
                    <div className="flex items-center gap-2 mt-2" data-id="element-1684">
                      <input type="checkbox" id="notifyClientEdit" className="rounded text-seform-orange focus:ring-seform-orange" checked={formData.notifyClient === 'Sì'} onChange={e => setFormData({
                    ...formData,
                    notifyClient: e.target.checked ? 'Sì' : 'No'
                  })} data-id="element-1685" />
                      <label htmlFor="notifyClientEdit" className="text-sm text-slate-700" data-id="element-1686">
                        Notifica cliente
                      </label>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1687">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-1688">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-1690" />} data-id="element-1689">
                      Salva scadenza
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.description || ''} deleteSuccess={deleteSuccess} data-id="element-1691" />
      </Layout>;
  }
  // ─── Contracts Section ────────────────────────────────────────────────────
  if (sectionId === 'contracts') {
    return <Layout data-id="element-1692">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-1693">
          <div className="overflow-x-auto" data-id="element-1694">
            <table className="w-full text-left border-collapse table-auto" data-id="element-1695">
              <thead data-id="element-1696">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1697">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-1698">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1699">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1700">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-1701">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('object') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1702">
                        {item.object}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-4 py-4" data-id="element-1703">
                        <Badge variant={item.type === 'Contratto' ? 'primary' : 'neutral'} data-id="element-1704">
                          {item.type}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('value') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1705">
                        {item.value}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-1706">
                        <Badge variant={item.status === 'Attivo' ? 'success' : item.status === 'In attesa' ? 'warning' : item.status === 'Scaduto' ? 'danger' : 'neutral'} data-id="element-1707">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1708">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-1709">
                        {actionButtons(item, 'contratto')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-1710">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-1711" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Dettaglio contratto" size="2xl" data-id="element-1712">
          {selectedItem && <div className="space-y-8" data-id="element-1713">
              <section data-id="element-1714">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1715">
                  <FileText className="h-4 w-4" data-id="element-1716" /> Dati generali
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-1717">
                  <div data-id="element-1718">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1719">
                      Codice
                    </span>
                    <p className="font-mono font-medium mt-1" data-id="element-1720">
                      {selectedItem.code}
                    </p>
                  </div>
                  <div data-id="element-1721">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1722">
                      Cliente
                    </span>
                    <p className="font-medium mt-1" data-id="element-1723">{clientName}</p>
                  </div>
                  <div data-id="element-1724">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1725">
                      Prodotto
                    </span>
                    <p className="font-medium mt-1" data-id="element-1726">{selectedItem.object}</p>
                  </div>
                  <div data-id="element-1727">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1728">
                      Tipologia
                    </span>
                    <p className="font-medium mt-1" data-id="element-1729">
                      {selectedItem.tipologia || 'Sicurezza'}
                    </p>
                  </div>
                  <div data-id="element-1730">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1731">
                      Responsabile sviluppo
                    </span>
                    <p className="font-medium mt-1" data-id="element-1732">
                      {selectedItem.responsabileSviluppo || 'Alessio'}
                    </p>
                  </div>
                  <div data-id="element-1733">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1734">
                      Provenienza
                    </span>
                    <p className="font-medium mt-1" data-id="element-1735">
                      {selectedItem.provenienza || 'Commerciale SEF'}
                    </p>
                  </div>
                  <div data-id="element-1736">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1737">
                      Corrispettivo provenienza
                    </span>
                    <p className="font-mono font-medium mt-1" data-id="element-1738">
                      {selectedItem.corrispettivoProvenienza || '€ 500'}
                    </p>
                  </div>
                </div>
              </section>

              <div className="h-px bg-slate-100" data-id="element-1739"></div>

              <section data-id="element-1740">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1741">
                  <AlertTriangle className="h-4 w-4" data-id="element-1742" /> Stato e workflow
                </h4>
                <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-1743">
                  <div data-id="element-1744">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1745">
                      Stato attuale
                    </span>
                    <div className="mt-1" data-id="element-1746">
                      <Badge variant={selectedItem.status === 'Attivo' || selectedItem.status === 'accettato' ? 'success' : selectedItem.status === 'In attesa' || selectedItem.status === 'in_valutazione' ? 'warning' : selectedItem.status === 'Scaduto' || selectedItem.status === 'non_accettato' || selectedItem.status === 'revocato' ? 'danger' : 'neutral'} data-id="element-1747">
                        {selectedItem.status}
                      </Badge>
                    </div>
                  </div>
                  <div data-id="element-1748">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1749">
                      Data cambio stato
                    </span>
                    <p className="font-medium mt-1" data-id="element-1750">
                      {selectedItem.dataCambioStato || selectedItem.date || '15/03/2025'}
                    </p>
                  </div>
                </div>

                {/* Conditional status-specific fields */}
                {['Attivo', 'accettato'].includes(selectedItem.status) && <div className="bg-green-50 p-4 rounded-lg border border-green-100" data-id="element-1751">
                    <h4 className="text-sm font-bold text-green-800 uppercase tracking-wider mb-3" data-id="element-1752">
                      Dettagli accettazione
                    </h4>
                    <div className="grid grid-cols-2 gap-4" data-id="element-1753">
                      <div data-id="element-1754">
                        <span className="text-xs font-semibold text-green-600 uppercase" data-id="element-1755">
                          Data di accettazione
                        </span>
                        <p className="font-medium text-green-900" data-id="element-1756">
                          {selectedItem.acceptanceDate || '15/03/2025'}
                        </p>
                      </div>
                    </div>
                  </div>}

                {['non_accettato', 'rejected'].includes(selectedItem.status) && <div className="bg-red-50 p-4 rounded-lg border border-red-100" data-id="element-1757">
                    <h4 className="text-sm font-bold text-red-800 uppercase tracking-wider mb-3" data-id="element-1758">
                      Dettagli non accettazione
                    </h4>
                    <div className="grid grid-cols-2 gap-4" data-id="element-1759">
                      <div data-id="element-1760">
                        <span className="text-xs font-semibold text-red-600 uppercase" data-id="element-1761">
                          Data non accettazione
                        </span>
                        <p className="font-medium text-red-900" data-id="element-1762">
                          {selectedItem.rejectionDate || '20/02/2025'}
                        </p>
                      </div>
                      <div data-id="element-1763">
                        <span className="text-xs font-semibold text-red-600 uppercase" data-id="element-1764">
                          Motivazione
                        </span>
                        <p className="font-medium text-red-900" data-id="element-1765">
                          {selectedItem.rejectionReason || 'Budget insufficiente'}
                        </p>
                      </div>
                    </div>
                  </div>}

                {['revocato', 'revoked'].includes(selectedItem.status) && <div className="bg-orange-50 p-4 rounded-lg border border-orange-100" data-id="element-1766">
                    <h4 className="text-sm font-bold text-orange-800 uppercase tracking-wider mb-3" data-id="element-1767">
                      Dettagli revoca
                    </h4>
                    <div className="grid grid-cols-2 gap-4" data-id="element-1768">
                      <div data-id="element-1769">
                        <span className="text-xs font-semibold text-orange-600 uppercase" data-id="element-1770">
                          Data revoca
                        </span>
                        <p className="font-medium text-orange-900" data-id="element-1771">
                          {selectedItem.revokeDate || '10/01/2025'}
                        </p>
                      </div>
                      <div data-id="element-1772">
                        <span className="text-xs font-semibold text-orange-600 uppercase" data-id="element-1773">
                          Motivazione
                        </span>
                        <p className="font-medium text-orange-900" data-id="element-1774">
                          {selectedItem.revokeReason || 'Cambio fornitore'}
                        </p>
                      </div>
                    </div>
                  </div>}
              </section>

              <div className="h-px bg-slate-100" data-id="element-1775"></div>

              <section data-id="element-1776">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1777">
                  <Euro className="h-4 w-4" data-id="element-1778" /> Dettagli economici
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-1779">
                  <div data-id="element-1780">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1781">
                      Importo
                    </span>
                    <p className="font-mono font-bold text-seform-navy mt-1" data-id="element-1782">
                      {selectedItem.value}
                    </p>
                  </div>
                  <div data-id="element-1783">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1784">
                      Durata (mesi)
                    </span>
                    <p className="font-medium mt-1" data-id="element-1785">
                      {selectedItem.durata || '12'}
                    </p>
                  </div>
                  <div data-id="element-1786">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1787">
                      Data preventivo
                    </span>
                    <p className="font-medium mt-1" data-id="element-1788">
                      {selectedItem.dataPreventivo || selectedItem.date || '10/01/2025'}
                    </p>
                  </div>
                  <div data-id="element-1789">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1790">
                      Scadenza
                    </span>
                    <p className="font-medium mt-1" data-id="element-1791">
                      {selectedItem.expiry || selectedItem.date}
                    </p>
                  </div>
                  <div data-id="element-1792">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1793">
                      Tacito rinnovo
                    </span>
                    <p className="font-medium mt-1" data-id="element-1794">
                      {selectedItem.tacitRenewal !== false ? 'Sì' : 'No'}
                    </p>
                  </div>
                </div>
              </section>

              <div className="flex justify-between items-center pt-4 border-t border-slate-100" data-id="element-1795">
                <div data-id="element-1796">
                  {['Attivo', 'accettato'].includes(selectedItem.status) && <Button variant="flat" color="success" startContent={<ClipboardList className="h-4 w-4" data-id="element-1798" />} onClick={() => setIsGenerateActivityOpen(true)} data-id="element-1797">
                      Genera attività operativa
                    </Button>}
                </div>
                <div className="flex gap-3" data-id="element-1799">
                  <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-1800">
                    Chiudi
                  </Button>
                  <Button onClick={() => {
                setIsViewOpen(false);
                handleEdit(selectedItem);
              }} startContent={<Edit className="h-4 w-4" data-id="element-1802" />} data-id="element-1801">
                    Modifica
                  </Button>
                </div>
              </div>
            </div>}
        </Modal>

        <GenerateActivityWizardModal isOpen={isGenerateActivityOpen} onClose={() => setIsGenerateActivityOpen(false)} data-id="element-1803" />

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica contratto' : 'Nuovo contratto'} size="2xl" data-id="element-1804">
          <AnimatePresence mode="wait" data-id="element-1805">
            {saveSuccess ? <SaveSuccessView label="Contratto salvato con successo" data-id="element-1806" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-1807">
                <div className="space-y-8" data-id="element-1808">
                  <section data-id="element-1809">
                    <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1810">
                      <FileText className="h-4 w-4" data-id="element-1811" /> Dati generali
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1812">
                      <ProgressiveNumberInput type="contract" value={formData.code || ''} onChange={val => setFormData({
                    ...formData,
                    code: val
                  })} disabled={!!selectedItem} data-id="element-1813" />
                      <Input label="Cliente" value={clientName} disabled data-id="element-1814" />
                      <Input label="Prodotto / Oggetto" value={formData.object || ''} onChange={e => setFormData({
                    ...formData,
                    object: e.target.value
                  })} required data-id="element-1815" />
                      <Select label="Tipologia" value={formData.tipologia || ''} onChange={e => setFormData({
                    ...formData,
                    tipologia: e.target.value
                  })} options={[{
                    value: '',
                    label: 'Seleziona...'
                  }, {
                    value: 'Sicurezza',
                    label: 'Sicurezza'
                  }, {
                    value: 'Privacy',
                    label: 'Privacy'
                  }, {
                    value: 'HACCP',
                    label: 'HACCP'
                  }, {
                    value: 'Altro',
                    label: 'Altro'
                  }]} data-id="element-1816" />
                      <Select label="Responsabile sviluppo" value={formData.responsabileSviluppo || ''} onChange={e => setFormData({
                    ...formData,
                    responsabileSviluppo: e.target.value
                  })} options={[{
                    value: 'alessio',
                    label: 'Alessio'
                  }, {
                    value: 'gianfranco',
                    label: 'Gian Franco'
                  }, {
                    value: 'lorenzo',
                    label: 'Lorenzo'
                  }, {
                    value: 'tiziano',
                    label: 'Tiziano'
                  }]} required data-id="element-1817" />
                      <Select label="Provenienza" value={formData.provenienza || ''} onChange={e => setFormData({
                    ...formData,
                    provenienza: e.target.value
                  })} options={[{
                    value: '',
                    label: 'Seleziona...'
                  }, {
                    value: 'Commerciale SEF',
                    label: 'Commerciale SEF'
                  }, {
                    value: 'Esterni e partner',
                    label: 'Esterni e partner'
                  }, {
                    value: 'Altro',
                    label: 'Altro'
                  }]} data-id="element-1818" />
                      <Input label="Corrispettivo provenienza (€)" placeholder="Es. 500,00" value={formData.corrispettivoProvenienza?.replace('€ ', '') || ''} onChange={e => setFormData({
                    ...formData,
                    corrispettivoProvenienza: e.target.value
                  })} data-id="element-1819" />
                    </div>
                  </section>

                  <div className="h-px bg-slate-100" data-id="element-1820"></div>

                  <section data-id="element-1821">
                    <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1822">
                      <AlertTriangle className="h-4 w-4" data-id="element-1823" /> Stato e workflow
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1824">
                      <Select label="Stato attuale" value={formData.status || ''} onChange={e => setFormData({
                    ...formData,
                    status: e.target.value
                  })} options={[{
                    value: 'da_proporre',
                    label: 'Da proporre'
                  }, {
                    value: 'in_valutazione',
                    label: 'In valutazione'
                  }, {
                    value: 'da_sollecitare',
                    label: 'Da sollecitare'
                  }, {
                    value: 'accettato',
                    label: 'Accettato'
                  }, {
                    value: 'non_accettato',
                    label: 'Non accettato'
                  }, {
                    value: 'revocato',
                    label: 'Revocato'
                  }, {
                    value: 'Attivo',
                    label: 'Attivo (Legacy)'
                  }, {
                    value: 'In attesa',
                    label: 'In attesa (Legacy)'
                  }, {
                    value: 'Bozza',
                    label: 'Bozza (Legacy)'
                  }, {
                    value: 'Scaduto',
                    label: 'Scaduto (Legacy)'
                  }]} required data-id="element-1825" />
                      <Input label="Data cambio stato" type="date" value={formData.dataCambioStato || new Date().toISOString().split('T')[0]} onChange={e => setFormData({
                    ...formData,
                    dataCambioStato: e.target.value
                  })} data-id="element-1826" />

                      {['accettato', 'Attivo'].includes(formData.status) && <Input label="Data di accettazione" type="date" value={formData.acceptanceDate || ''} onChange={e => setFormData({
                    ...formData,
                    acceptanceDate: e.target.value
                  })} data-id="element-1827" />}

                      {['non_accettato', 'rejected'].includes(formData.status) && <>
                          <Input label="Data non accettazione" type="date" value={formData.rejectionDate || ''} onChange={e => setFormData({
                      ...formData,
                      rejectionDate: e.target.value
                    })} data-id="element-1828" />
                          <div className="col-span-full" data-id="element-1829">
                            <Input label="Motivazione" placeholder="Inserisci la motivazione..." value={formData.rejectionReason || ''} onChange={e => setFormData({
                        ...formData,
                        rejectionReason: e.target.value
                      })} data-id="element-1830" />
                          </div>
                        </>}

                      {['revocato', 'revoked'].includes(formData.status) && <>
                          <Input label="Data revoca" type="date" value={formData.revokeDate || ''} onChange={e => setFormData({
                      ...formData,
                      revokeDate: e.target.value
                    })} data-id="element-1831" />
                          <div className="col-span-full" data-id="element-1832">
                            <Input label="Motivazione" placeholder="Inserisci la motivazione della revoca..." value={formData.revokeReason || ''} onChange={e => setFormData({
                        ...formData,
                        revokeReason: e.target.value
                      })} data-id="element-1833" />
                          </div>
                        </>}
                    </div>
                  </section>

                  <div className="h-px bg-slate-100" data-id="element-1834"></div>

                  <section data-id="element-1835">
                    <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1836">
                      <Euro className="h-4 w-4" data-id="element-1837" /> Dettagli economici
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1838">
                      <Input label="Importo (€)" type="number" placeholder="0.00" value={formData.value?.replace('€ ', '') || ''} onChange={e => setFormData({
                    ...formData,
                    value: e.target.value
                  })} required data-id="element-1839" />
                      <Input label="Durata (mesi)" type="number" value={formData.durata || '12'} onChange={e => setFormData({
                    ...formData,
                    durata: e.target.value
                  })} required data-id="element-1840" />
                      <Input label="Data preventivo" type="date" value={formData.dataPreventivo || formData.date || ''} onChange={e => setFormData({
                    ...formData,
                    dataPreventivo: e.target.value
                  })} data-id="element-1841" />
                      <div className="flex items-center gap-2 pt-6" data-id="element-1842">
                        <input type="checkbox" id="renewal" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={formData.tacitRenewal ?? false} onChange={e => setFormData({
                      ...formData,
                      tacitRenewal: e.target.checked
                    })} data-id="element-1843" />
                        <label htmlFor="renewal" className="text-sm text-slate-700" data-id="element-1844">
                          Tacito rinnovo
                        </label>
                      </div>
                    </div>
                  </section>

                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1845">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-1846">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-1848" />} data-id="element-1847">
                      Salva contratto
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.code || ''} deleteSuccess={deleteSuccess} data-id="element-1849" />
      </Layout>;
  }
  // ─── Invoices Section ─────────────────────────────────────────────────────
  if (sectionId === 'invoices') {
    return <Layout data-id="element-1850">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-1851">
          <div className="overflow-x-auto" data-id="element-1852">
            <table className="w-full text-left border-collapse table-auto" data-id="element-1853">
              <thead data-id="element-1854">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1855">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-1856">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1857">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1858">
                    {visibleColumns.includes('number') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-1859">
                        {item.number}
                      </td>}
                    {visibleColumns.includes('description') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1860">
                        {item.description}
                      </td>}
                    {visibleColumns.includes('amount') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1861">
                        {item.amount}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-1862">
                        <Badge variant={item.status === 'Pagata' ? 'success' : item.status === 'In attesa' ? 'warning' : item.status === 'Scaduta' ? 'danger' : 'neutral'} data-id="element-1863">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('dueDate') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1864">
                        {item.dueDate}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-1865">
                        {actionButtons(item, 'fattura')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-1866">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-1867" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Dettaglio fattura" size="lg" data-id="element-1868">
          {selectedItem && <div className="space-y-6" data-id="element-1869">
              <div className="grid grid-cols-2 gap-4" data-id="element-1870">
                <div data-id="element-1871">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1872">
                    Numero
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-1873">
                    {selectedItem.number}
                  </p>
                </div>
                <div data-id="element-1874">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1875">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-1876">
                    <Badge variant={selectedItem.status === 'Pagata' ? 'success' : selectedItem.status === 'In attesa' ? 'warning' : 'danger'} data-id="element-1877">
                      {selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-1878">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1879">
                    Descrizione
                  </span>
                  <p className="font-medium mt-1" data-id="element-1880">{selectedItem.description}</p>
                </div>
                <div data-id="element-1881">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1882">
                    Importo
                  </span>
                  <p className="font-medium mt-1" data-id="element-1883">{selectedItem.amount}</p>
                </div>
                <div data-id="element-1884">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1885">
                    Scadenza
                  </span>
                  <p className="font-medium mt-1" data-id="element-1886">{selectedItem.dueDate}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1887">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-1888">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-1890" />} data-id="element-1889">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica fattura' : 'Nuova fattura'} size="lg" data-id="element-1891">
          <AnimatePresence mode="wait" data-id="element-1892">
            {saveSuccess ? <SaveSuccessView label="Fattura salvata con successo" data-id="element-1893" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-1894">
                <div className="space-y-6" data-id="element-1895">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1896">
                    <Input label="Numero" value={formData.number || ''} onChange={e => setFormData({
                  ...formData,
                  number: e.target.value
                })} data-id="element-1897" />
                    <Input label="Importo" value={formData.amount || ''} onChange={e => setFormData({
                  ...formData,
                  amount: e.target.value
                })} data-id="element-1898" />
                    <Input label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                  ...formData,
                  description: e.target.value
                })} required className="col-span-full" data-id="element-1899" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Bozza',
                  label: 'Bozza'
                }, {
                  value: 'In attesa',
                  label: 'In attesa'
                }, {
                  value: 'Pagata',
                  label: 'Pagata'
                }, {
                  value: 'Scaduta',
                  label: 'Scaduta'
                }]} required data-id="element-1900" />
                    <Input label="Data scadenza" type="date" value={formData.dueDate || ''} onChange={e => setFormData({
                  ...formData,
                  dueDate: e.target.value
                })} data-id="element-1901" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1902">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-1903">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-1905" />} data-id="element-1904">
                      Salva fattura
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.number || ''} deleteSuccess={deleteSuccess} data-id="element-1906" />
      </Layout>;
  }
  // ─── Training Section ─────────────────────────────────────────────────────
  if (sectionId === 'training') {
    return <Layout data-id="element-1907">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-1908">
          <div className="overflow-x-auto" data-id="element-1909">
            <table className="w-full text-left border-collapse table-auto" data-id="element-1910">
              <thead data-id="element-1911">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1912">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-1913">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1914">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1915">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-1916">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('title') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1917">
                        {item.title}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1918">
                        {item.type}
                      </td>}
                    {visibleColumns.includes('mode') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1919">
                        {item.mode}
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1920">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('enrolled') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1921">
                        {item.enrolled}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-1922">
                        <Badge variant={item.status === 'In corso' ? 'success' : item.status === 'Programmato' ? 'info' : 'neutral'} data-id="element-1923">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-1924">
                        {actionButtons(item, 'corso')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-1925">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-1926" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Dettaglio corso" size="lg" data-id="element-1927">
          {selectedItem && <div className="space-y-6" data-id="element-1928">
              <div className="grid grid-cols-2 gap-4" data-id="element-1929">
                <div data-id="element-1930">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1931">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-1932">
                    {selectedItem.code}
                  </p>
                </div>
                <div data-id="element-1933">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1934">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-1935">
                    <Badge variant={selectedItem.status === 'In corso' ? 'success' : selectedItem.status === 'Programmato' ? 'info' : 'neutral'} data-id="element-1936">
                      {selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-1937">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1938">
                    Titolo
                  </span>
                  <p className="font-medium text-lg mt-1" data-id="element-1939">
                    {selectedItem.title}
                  </p>
                </div>
                <div data-id="element-1940">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1941">
                    Tipologia
                  </span>
                  <p className="font-medium mt-1" data-id="element-1942">{selectedItem.type}</p>
                </div>
                <div data-id="element-1943">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1944">
                    Modalità
                  </span>
                  <p className="font-medium mt-1" data-id="element-1945">{selectedItem.mode}</p>
                </div>
                <div data-id="element-1946">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1947">
                    Data
                  </span>
                  <p className="font-medium mt-1" data-id="element-1948">{selectedItem.date}</p>
                </div>
                <div data-id="element-1949">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1950">
                    Iscritti
                  </span>
                  <p className="font-medium mt-1" data-id="element-1951">{selectedItem.enrolled}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1952">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-1953">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-1955" />} data-id="element-1954">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica corso' : 'Nuovo corso'} size="lg" data-id="element-1956">
          <AnimatePresence mode="wait" data-id="element-1957">
            {saveSuccess ? <SaveSuccessView label="Corso salvato con successo" data-id="element-1958" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-1959">
                <div className="space-y-6" data-id="element-1960">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1961">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} disabled={!!selectedItem} data-id="element-1962" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Programmato',
                  label: 'Programmato'
                }, {
                  value: 'In corso',
                  label: 'In corso'
                }, {
                  value: 'Completato',
                  label: 'Completato'
                }]} required data-id="element-1963" />
                    <Input label="Titolo" value={formData.title || ''} onChange={e => setFormData({
                  ...formData,
                  title: e.target.value
                })} required className="col-span-full" data-id="element-1964" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Sicurezza',
                  label: 'Sicurezza'
                }, {
                  value: 'HACCP',
                  label: 'HACCP'
                }, {
                  value: 'Privacy',
                  label: 'Privacy'
                }]} required data-id="element-1965" />
                    <Select label="Modalità" value={formData.mode || ''} onChange={e => setFormData({
                  ...formData,
                  mode: e.target.value
                })} options={[{
                  value: 'Sede SEF',
                  label: 'Sede SEF'
                }, {
                  value: 'Sede cliente',
                  label: 'Sede cliente'
                }, {
                  value: 'A distanza',
                  label: 'A distanza'
                }]} required data-id="element-1966" />
                    <DatePicker label="Data" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-1967" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1968">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-1969">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-1971" />} data-id="element-1970">
                      Salva corso
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.title || ''} deleteSuccess={deleteSuccess} data-id="element-1972" />
      </Layout>;
  }
  // ─── Operations Section ───────────────────────────────────────────────────
  if (sectionId === 'operations') {
    return <Layout data-id="element-1973">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-1974">
          <div className="overflow-x-auto" data-id="element-1975">
            <table className="w-full text-left border-collapse table-auto" data-id="element-1976">
              <thead data-id="element-1977">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1978">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-1979">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1980">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1981">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-1982">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('activityType') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-1983">
                        {item.activityType}
                      </td>}
                    {visibleColumns.includes('ambito') && <td className="px-4 py-4" data-id="element-1984">
                        <Badge variant="neutral" className="bg-slate-100 text-slate-600 border-slate-200" data-id="element-1985">
                          {item.ambito}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('contract') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-1986">
                        {item.contract}
                      </td>}
                    {visibleColumns.includes('operator') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1987">
                        {item.operator}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-1988">
                        <Badge variant={statusColors[item.status] as any || 'neutral'} data-id="element-1989">
                          {statusLabels[item.status] || item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('deadline') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-1990">
                        {item.deadline}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-1991">
                        {actionButtons(item, 'attività')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-1992">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-1993" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedItem ? `Dettaglio attività — ${selectedItem.code}` : ''} size="lg" data-id="element-1994">
          {selectedItem && <div className="space-y-6" data-id="element-1995">
              <div className="grid grid-cols-2 gap-4" data-id="element-1996">
                <div data-id="element-1997">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1998">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-1999">
                    {selectedItem.code}
                  </p>
                </div>
                <div data-id="element-2000">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2001">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-2002">
                    <Badge variant={statusColors[selectedItem.status] as any || 'neutral'} data-id="element-2003">
                      {statusLabels[selectedItem.status] || selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-2004">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2005">
                    Tipologia attività
                  </span>
                  <p className="font-medium mt-1" data-id="element-2006">
                    {selectedItem.activityType}
                  </p>
                </div>
                <div data-id="element-2007">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2008">
                    Ambito
                  </span>
                  <p className="font-medium mt-1" data-id="element-2009">{selectedItem.ambito}</p>
                </div>
                <div data-id="element-2010">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2011">
                    Contratto
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2012">
                    {selectedItem.contract}
                  </p>
                </div>
                <div data-id="element-2013">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2014">
                    Operatore
                  </span>
                  <p className="font-medium mt-1" data-id="element-2015">{selectedItem.operator}</p>
                </div>
                <div data-id="element-2016">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2017">
                    Zona
                  </span>
                  <p className="font-medium mt-1" data-id="element-2018">{selectedItem.zone}</p>
                </div>
                <div data-id="element-2019">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2020">
                    Scadenza
                  </span>
                  <p className="font-medium mt-1" data-id="element-2021">{selectedItem.deadline}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2022">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2023">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-2025" />} data-id="element-2024">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica attività' : 'Nuova attività'} size="lg" data-id="element-2026">
          <AnimatePresence mode="wait" data-id="element-2027">
            {saveSuccess ? <SaveSuccessView label="Attività salvata con successo" data-id="element-2028" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2029">
                <div className="space-y-6" data-id="element-2030">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2031">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} data-id="element-2032" />
                    <Select label="Tipologia attività" value={formData.activityType || ''} onChange={e => setFormData({
                  ...formData,
                  activityType: e.target.value
                })} options={['Sopralluogo periodico', 'Sopralluogo iniziale', 'Backoffice', 'Riunione in ufficio', 'Consulenza via email'].map(t => ({
                  value: t,
                  label: t
                }))} required data-id="element-2033" />
                    <Select label="Ambito" value={formData.ambito || ''} onChange={e => setFormData({
                  ...formData,
                  ambito: e.target.value
                })} options={['Sicurezza', 'Privacy', 'HACCP', 'Ambiente', 'Formazione'].map(a => ({
                  value: a,
                  label: a
                }))} required data-id="element-2034" />
                    <Select label="Operatore" value={formData.operator || ''} onChange={e => setFormData({
                  ...formData,
                  operator: e.target.value
                })} options={[{
                  value: 'Alessio',
                  label: 'Alessio'
                }, {
                  value: 'Lorenzo',
                  label: 'Lorenzo'
                }, {
                  value: 'Gian Franco',
                  label: 'Gian Franco'
                }, {
                  value: 'Tiziano',
                  label: 'Tiziano'
                }]} required data-id="element-2035" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={Object.entries(statusLabels).map(([v, l]) => ({
                  value: v,
                  label: l
                }))} required data-id="element-2036" />
                    <DatePicker label="Scadenza" value={formData.deadline || ''} onChange={e => setFormData({
                  ...formData,
                  deadline: e.target.value
                })} data-id="element-2037" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2038">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2039">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2041" />} data-id="element-2040">
                      Salva attività
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.code || ''} deleteSuccess={deleteSuccess} data-id="element-2042" />
      </Layout>;
  }
  // ─── Non Conformities Section ─────────────────────────────────────────────
  if (sectionId === 'non_conformities') {
    return <Layout data-id="element-2043">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-2044">
          <div className="overflow-x-auto" data-id="element-2045">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2046">
              <thead data-id="element-2047">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2048">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-2049">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2050">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2051">
                    {visibleColumns.includes('number') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2052">
                        {item.number}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-4 py-4" data-id="element-2053">
                        <Badge variant={item.type === 'Non conformità maggiore' ? 'danger' : item.type === 'Non conformità minore' ? 'warning' : 'info'} data-id="element-2054">
                          {item.type}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('origin') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2055">
                        {item.origin}
                      </td>}
                    {visibleColumns.includes('description') && <td className="px-4 py-4 text-sm text-slate-600 max-w-xs truncate" data-id="element-2056">
                        {item.description}
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2057">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2058">
                        {item.responsible}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-2059">
                        <Badge variant={item.status === 'Aperta' ? 'danger' : item.status === 'Sospesa' ? 'warning' : 'success'} data-id="element-2060">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2061">
                        <div className="flex items-center gap-1" data-id="element-2062">
                          <Tooltip content="Visualizza NC" data-id="element-2063">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-2064">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-2065" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Invia report" data-id="element-2066">
                            <Button variant="light" size="sm" className="!px-1" data-id="element-2067">
                              <Mail className="h-4 w-4 text-blue-500" data-id="element-2068" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Modifica NC" data-id="element-2069">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-2070">
                              <Edit className="h-4 w-4 text-seform-orange" data-id="element-2071" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Elimina NC" data-id="element-2072">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-2073">
                              <Trash2 className="h-4 w-4 text-red-500" data-id="element-2074" />
                            </Button>
                          </Tooltip>
                        </div>
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2075">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2076" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedItem ? `Dettaglio NC — ${selectedItem.number}` : ''} size="lg" data-id="element-2077">
          {selectedItem && <div className="space-y-6" data-id="element-2078">
              <div className="grid grid-cols-2 gap-4" data-id="element-2079">
                <div data-id="element-2080">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2081">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2082">
                    {selectedItem.number}
                  </p>
                </div>
                <div data-id="element-2083">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2084">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-2085">
                    <Badge variant={selectedItem.status === 'Aperta' ? 'danger' : selectedItem.status === 'Sospesa' ? 'warning' : 'success'} data-id="element-2086">
                      {selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-2087">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2088">
                    Tipologia
                  </span>
                  <div className="mt-1" data-id="element-2089">
                    <Badge variant={selectedItem.type === 'Non conformità maggiore' ? 'danger' : 'warning'} data-id="element-2090">
                      {selectedItem.type}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-2091">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2092">
                    Origine
                  </span>
                  <p className="font-medium mt-1" data-id="element-2093">{selectedItem.origin}</p>
                </div>
                <div className="col-span-full" data-id="element-2094">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2095">
                    Descrizione
                  </span>
                  <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 border border-slate-100 mt-1" data-id="element-2096">
                    {selectedItem.description}
                  </p>
                </div>
                <div data-id="element-2097">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2098">
                    Data rilevazione
                  </span>
                  <p className="font-medium mt-1" data-id="element-2099">{selectedItem.date}</p>
                </div>
                <div data-id="element-2100">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2101">
                    Responsabile
                  </span>
                  <p className="font-medium mt-1" data-id="element-2102">{selectedItem.responsible}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2103">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2104">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-2106" />} data-id="element-2105">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica non conformità' : 'Nuova non conformità'} size="lg" data-id="element-2107">
          <AnimatePresence mode="wait" data-id="element-2108">
            {saveSuccess ? <SaveSuccessView label="Non conformità salvata con successo" data-id="element-2109" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2110">
                <div className="space-y-6" data-id="element-2111">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2112">
                    <Input label="Codice" value={formData.number || ''} onChange={e => setFormData({
                  ...formData,
                  number: e.target.value
                })} disabled={!!selectedItem} data-id="element-2113" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Non conformità maggiore',
                  label: 'Non conformità maggiore'
                }, {
                  value: 'Non conformità minore',
                  label: 'Non conformità minore'
                }, {
                  value: 'Osservazione',
                  label: 'Osservazione'
                }]} required data-id="element-2114" />
                    <Select label="Origine" value={formData.origin || ''} onChange={e => setFormData({
                  ...formData,
                  origin: e.target.value
                })} options={[{
                  value: 'Interna',
                  label: 'Interna'
                }, {
                  value: 'Cliente (reclamo)',
                  label: 'Cliente (reclamo)'
                }, {
                  value: 'Fornitore',
                  label: 'Fornitore'
                }]} required data-id="element-2115" />
                    <DatePicker label="Data rilevazione" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-2116" />
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-2117" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Aperta',
                  label: 'Aperta'
                }, {
                  value: 'Sospesa',
                  label: 'Sospesa'
                }, {
                  value: 'Chiusa',
                  label: 'Chiusa'
                }]} required data-id="element-2118" />
                    <div className="col-span-full" data-id="element-2119">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} required data-id="element-2120" />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2121">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2122">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2124" />} data-id="element-2123">
                      Salva
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.number || ''} deleteSuccess={deleteSuccess} data-id="element-2125" />
      </Layout>;
  }
  // ─── Improvements Section ─────────────────────────────────────────────────
  if (sectionId === 'improvements') {
    return <Layout data-id="element-2126">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-2127">
          <div className="overflow-x-auto" data-id="element-2128">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2129">
              <thead data-id="element-2130">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2131">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-2132">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2133">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2134">
                    {visibleColumns.includes('number') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2135">
                        {item.number}
                      </td>}
                    {visibleColumns.includes('subject') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2136">
                        {item.subject}
                      </td>}
                    {visibleColumns.includes('description') && <td className="px-4 py-4 text-sm text-slate-600 max-w-xs truncate" data-id="element-2137">
                        {item.description}
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2138">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2139">
                        {item.responsible}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-2140">
                        <Badge variant={item.status === 'Approvato' ? 'success' : item.status === 'Sospesa' ? 'warning' : 'info'} data-id="element-2141">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2142">
                        {actionButtons(item, 'miglioramento')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2143">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2144" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedItem ? `Dettaglio miglioramento — ${selectedItem.number}` : ''} size="lg" data-id="element-2145">
          {selectedItem && <div className="space-y-6" data-id="element-2146">
              <div className="grid grid-cols-2 gap-4" data-id="element-2147">
                <div data-id="element-2148">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2149">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2150">
                    {selectedItem.number}
                  </p>
                </div>
                <div data-id="element-2151">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2152">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-2153">
                    <Badge variant={selectedItem.status === 'Approvato' ? 'success' : selectedItem.status === 'Sospesa' ? 'warning' : 'info'} data-id="element-2154">
                      {selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-2155">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2156">
                    Oggetto
                  </span>
                  <p className="font-medium mt-1" data-id="element-2157">{selectedItem.subject}</p>
                </div>
                <div className="col-span-full" data-id="element-2158">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2159">
                    Descrizione
                  </span>
                  <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 border border-slate-100 mt-1" data-id="element-2160">
                    {selectedItem.description}
                  </p>
                </div>
                <div data-id="element-2161">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2162">
                    Data proposta
                  </span>
                  <p className="font-medium mt-1" data-id="element-2163">{selectedItem.date}</p>
                </div>
                <div data-id="element-2164">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2165">
                    Responsabile
                  </span>
                  <p className="font-medium mt-1" data-id="element-2166">{selectedItem.responsible}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2167">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2168">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-2170" />} data-id="element-2169">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica miglioramento' : 'Nuovo miglioramento'} size="lg" data-id="element-2171">
          <AnimatePresence mode="wait" data-id="element-2172">
            {saveSuccess ? <SaveSuccessView label="Miglioramento salvato con successo" data-id="element-2173" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2174">
                <div className="space-y-6" data-id="element-2175">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2176">
                    <Input label="Codice" value={formData.number || ''} onChange={e => setFormData({
                  ...formData,
                  number: e.target.value
                })} disabled={!!selectedItem} data-id="element-2177" />
                    <DatePicker label="Data proposta" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-2178" />
                    <div className="col-span-full" data-id="element-2179">
                      <Input label="Oggetto" value={formData.subject || ''} onChange={e => setFormData({
                    ...formData,
                    subject: e.target.value
                  })} required data-id="element-2180" />
                    </div>
                    <div className="col-span-full" data-id="element-2181">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} required data-id="element-2182" />
                    </div>
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-2183" />
                    <Select label="Stato" value={formData.status || ''} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'In valutazione',
                  label: 'In valutazione'
                }, {
                  value: 'Approvato',
                  label: 'Approvato'
                }, {
                  value: 'Sospesa',
                  label: 'Sospesa'
                }, {
                  value: 'Scartata',
                  label: 'Scartata'
                }]} required data-id="element-2184" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2185">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2186">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2188" />} data-id="element-2187">
                      Salva
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.number || ''} deleteSuccess={deleteSuccess} data-id="element-2189" />
      </Layout>;
  }
  // ─── Equipment Section ────────────────────────────────────────────────────
  if (sectionId === 'equipment') {
    return <Layout data-id="element-2190">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-2191">
          <div className="overflow-x-auto" data-id="element-2192">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2193">
              <thead data-id="element-2194">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2195">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-2196">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2197">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2198">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2199">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('name') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2200">
                        {item.name}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2201">
                        {item.type}
                      </td>}
                    {visibleColumns.includes('lastMaintenance') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2202">
                        {item.lastMaintenance}
                      </td>}
                    {visibleColumns.includes('nextMaintenance') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2203">
                        {item.nextMaintenance}
                      </td>}
                    {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2204">
                        {item.responsible}
                      </td>}
                    {visibleColumns.includes('maintenanceType') && <td className="px-4 py-4" data-id="element-2205">
                        <Badge variant={item.maintenanceType === 'Ordinaria' ? 'info' : 'warning'} data-id="element-2206">
                          {item.maintenanceType}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2207">
                        <div className="flex items-center gap-1" data-id="element-2208">
                          <Tooltip content="Visualizza macchinario" data-id="element-2209">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-2210">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-2211" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Registra manutenzione" data-id="element-2212">
                            <Button variant="light" size="sm" className="!px-1" data-id="element-2213">
                              <Wrench className="h-4 w-4 text-blue-500" data-id="element-2214" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Modifica macchinario" data-id="element-2215">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-2216">
                              <Edit className="h-4 w-4 text-seform-orange" data-id="element-2217" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Elimina macchinario" data-id="element-2218">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-2219">
                              <Trash2 className="h-4 w-4 text-red-500" data-id="element-2220" />
                            </Button>
                          </Tooltip>
                        </div>
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2221">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2222" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedItem ? `Dettaglio macchinario — ${selectedItem.code}` : ''} size="lg" data-id="element-2223">
          {selectedItem && <div className="space-y-6" data-id="element-2224">
              <div className="grid grid-cols-2 gap-4" data-id="element-2225">
                <div data-id="element-2226">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2227">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2228">
                    {selectedItem.code}
                  </p>
                </div>
                <div data-id="element-2229">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2230">
                    Tipologia
                  </span>
                  <p className="font-medium mt-1" data-id="element-2231">{selectedItem.type}</p>
                </div>
                <div className="col-span-full" data-id="element-2232">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2233">
                    Nome
                  </span>
                  <p className="font-medium mt-1" data-id="element-2234">{selectedItem.name}</p>
                </div>
                <div data-id="element-2235">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2236">
                    Responsabile
                  </span>
                  <p className="font-medium mt-1" data-id="element-2237">{selectedItem.responsible}</p>
                </div>
                <div data-id="element-2238">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2239">
                    Tipo manutenzione
                  </span>
                  <div className="mt-1" data-id="element-2240">
                    <Badge variant={selectedItem.maintenanceType === 'Ordinaria' ? 'info' : 'warning'} data-id="element-2241">
                      {selectedItem.maintenanceType}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-2242">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2243">
                    Ultima manutenzione
                  </span>
                  <p className="font-medium mt-1" data-id="element-2244">
                    {selectedItem.lastMaintenance}
                  </p>
                </div>
                <div data-id="element-2245">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2246">
                    Prossima scadenza
                  </span>
                  <p className="font-medium mt-1" data-id="element-2247">
                    {selectedItem.nextMaintenance}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2248">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2249">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-2251" />} data-id="element-2250">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica macchinario' : 'Nuovo macchinario'} size="lg" data-id="element-2252">
          <AnimatePresence mode="wait" data-id="element-2253">
            {saveSuccess ? <SaveSuccessView label="Macchinario salvato con successo" data-id="element-2254" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2255">
                <div className="space-y-6" data-id="element-2256">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2257">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} data-id="element-2258" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Strumento Misura',
                  label: 'Strumento Misura'
                }, {
                  value: 'Hardware',
                  label: 'Hardware'
                }, {
                  value: 'Veicolo',
                  label: 'Veicolo'
                }, {
                  value: 'Attrezzatura',
                  label: 'Attrezzatura'
                }]} required data-id="element-2259" />
                    <Input label="Nome" value={formData.name || ''} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required className="col-span-full" data-id="element-2260" />
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-2261" />
                    <Select label="Tipo manutenzione" value={formData.maintenanceType || ''} onChange={e => setFormData({
                  ...formData,
                  maintenanceType: e.target.value
                })} options={[{
                  value: 'Ordinaria',
                  label: 'Ordinaria'
                }, {
                  value: 'Straordinaria',
                  label: 'Straordinaria'
                }]} data-id="element-2262" />
                    <DatePicker label="Prossima scadenza" value={formData.nextMaintenance || ''} onChange={e => setFormData({
                  ...formData,
                  nextMaintenance: e.target.value
                })} data-id="element-2263" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2264">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2265">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2267" />} data-id="element-2266">
                      Salva macchinario
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.name || ''} deleteSuccess={deleteSuccess} data-id="element-2268" />
      </Layout>;
  }
  // ─── Maintenance Section ──────────────────────────────────────────────────
  if (sectionId === 'maintenance') {
    return <Layout data-id="element-2269">
        {header}
        {toolbar}
        <Card noPadding className="overflow-hidden" data-id="element-2270">
          <div className="overflow-x-auto" data-id="element-2271">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2272">
              <thead data-id="element-2273">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2274">
                  {cols.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className="px-4 py-4" data-id="element-2275">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2276">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2277">
                    {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2278">
                        {item.code}
                      </td>}
                    {visibleColumns.includes('equipment') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2279">
                        {item.equipment}
                      </td>}
                    {visibleColumns.includes('type') && <td className="px-4 py-4" data-id="element-2280">
                        <Badge variant={item.type === 'Ordinaria' ? 'info' : 'warning'} data-id="element-2281">
                          {item.type}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2282">
                        {item.date}
                      </td>}
                    {visibleColumns.includes('result') && <td className="px-4 py-4" data-id="element-2283">
                        <Badge variant={item.result === 'Positivo' ? 'success' : 'danger'} data-id="element-2284">
                          {item.result}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('nextDate') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2285">
                        {item.nextDate}
                      </td>}
                    {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2286">
                        {item.responsible}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2287">
                        {actionButtons(item, 'manutenzione')}
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2288">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2289" />
          </div>
        </Card>

        <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedItem ? `Dettaglio manutenzione — ${selectedItem.code}` : ''} size="lg" data-id="element-2290">
          {selectedItem && <div className="space-y-6" data-id="element-2291">
              <div className="grid grid-cols-2 gap-4" data-id="element-2292">
                <div data-id="element-2293">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2294">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2295">
                    {selectedItem.code}
                  </p>
                </div>
                <div data-id="element-2296">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2297">
                    Esito
                  </span>
                  <div className="mt-1" data-id="element-2298">
                    <Badge variant={selectedItem.result === 'Positivo' ? 'success' : 'danger'} data-id="element-2299">
                      {selectedItem.result}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-full" data-id="element-2300">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2301">
                    Macchinario
                  </span>
                  <p className="font-medium mt-1" data-id="element-2302">{selectedItem.equipment}</p>
                </div>
                <div data-id="element-2303">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2304">
                    Tipo
                  </span>
                  <div className="mt-1" data-id="element-2305">
                    <Badge variant={selectedItem.type === 'Ordinaria' ? 'info' : 'warning'} data-id="element-2306">
                      {selectedItem.type}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-2307">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2308">
                    Data esecuzione
                  </span>
                  <p className="font-medium mt-1" data-id="element-2309">{selectedItem.date}</p>
                </div>
                <div data-id="element-2310">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2311">
                    Prossima scadenza
                  </span>
                  <p className="font-medium mt-1" data-id="element-2312">{selectedItem.nextDate}</p>
                </div>
                <div data-id="element-2313">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2314">
                    Responsabile
                  </span>
                  <p className="font-medium mt-1" data-id="element-2315">{selectedItem.responsible}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2316">
                <Button variant="flat" color="default" onClick={() => setIsViewOpen(false)} data-id="element-2317">
                  Chiudi
                </Button>
                <Button onClick={() => {
              setIsViewOpen(false);
              handleEdit(selectedItem);
            }} startContent={<Edit className="h-4 w-4" data-id="element-2319" />} data-id="element-2318">
                  Modifica
                </Button>
              </div>
            </div>}
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title={selectedItem ? 'Modifica manutenzione' : 'Nuova manutenzione'} size="lg" data-id="element-2320">
          <AnimatePresence mode="wait" data-id="element-2321">
            {saveSuccess ? <SaveSuccessView label="Manutenzione salvata con successo" data-id="element-2322" /> : <motion.div key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-2323">
                <div className="space-y-6" data-id="element-2324">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2325">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} data-id="element-2326" />
                    <Input label="Macchinario" value={formData.equipment || ''} onChange={e => setFormData({
                  ...formData,
                  equipment: e.target.value
                })} required data-id="element-2327" />
                    <Select label="Tipo" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Ordinaria',
                  label: 'Ordinaria'
                }, {
                  value: 'Straordinaria',
                  label: 'Straordinaria'
                }]} required data-id="element-2328" />
                    <DatePicker label="Data esecuzione" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-2329" />
                    <Select label="Esito" value={formData.result || ''} onChange={e => setFormData({
                  ...formData,
                  result: e.target.value
                })} options={[{
                  value: 'Positivo',
                  label: 'Positivo'
                }, {
                  value: 'Negativo',
                  label: 'Negativo'
                }]} required data-id="element-2330" />
                    <DatePicker label="Prossima scadenza" value={formData.nextDate || ''} onChange={e => setFormData({
                  ...formData,
                  nextDate: e.target.value
                })} data-id="element-2331" />
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} className="col-span-full" data-id="element-2332" />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2333">
                    <Button variant="flat" color="default" onClick={() => setIsEditOpen(false)} data-id="element-2334">
                      Annulla
                    </Button>
                    <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2336" />} data-id="element-2335">
                      Salva manutenzione
                    </Button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </Modal>

        <DeleteModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={confirmDelete} itemLabel={selectedItem?.code || ''} deleteSuccess={deleteSuccess} data-id="element-2337" />
      </Layout>;
  }
  // Fallback
  return <Layout data-id="element-2338">
      {header}
      <Card data-id="element-2339">
        <p className="text-slate-500 text-center py-8" data-id="element-2340">
          Sezione non disponibile.
        </p>
      </Card>
    </Layout>;
}
// ─── Section Filters Component ────────────────────────────────────────────────
function SectionFilters({
  sectionId,
  filters,
  setFilter
}: {
  sectionId: string;
  filters: any;
  setFilter: (key: string, value: any) => void;
}) {
  switch (sectionId) {
    case 'workers':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2341">
          <Select label="Stato formazione" value={filters.trainingStatus || ''} onChange={e => setFilter('trainingStatus', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'valid',
          label: 'In regola'
        }, {
          value: 'expiring',
          label: 'In scadenza'
        }, {
          value: 'expired',
          label: 'Scaduto'
        }]} data-id="element-2342" />
          <Select label="Sorveglianza" value={filters.healthStatus || ''} onChange={e => setFilter('healthStatus', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'fit',
          label: 'Idoneo'
        }, {
          value: 'unfit',
          label: 'Non idoneo'
        }]} data-id="element-2343" />
        </div>;
    case 'deadlines':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2344">
          <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Riunione annuale',
          label: 'Riunione annuale'
        }, {
          value: 'Prova di emergenza',
          label: 'Prova di emergenza'
        }, {
          value: 'Tampone alimentare',
          label: 'Tampone alimentare'
        }, {
          value: 'Audit interno',
          label: 'Audit interno'
        }, {
          value: 'Report annuale',
          label: 'Report annuale'
        }]} data-id="element-2345" />
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Urgente',
          label: 'Urgente'
        }, {
          value: 'In scadenza',
          label: 'In scadenza'
        }, {
          value: 'Pianificata',
          label: 'Pianificata'
        }, {
          value: 'Completata',
          label: 'Completata'
        }]} data-id="element-2346" />
          <Select label="Responsabile" value={filters.responsible || ''} onChange={e => setFilter('responsible', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Alessio',
          label: 'Alessio'
        }, {
          value: 'Lorenzo',
          label: 'Lorenzo'
        }, {
          value: 'Gian Franco',
          label: 'Gian Franco'
        }, {
          value: 'Tiziano',
          label: 'Tiziano'
        }]} data-id="element-2347" />
        </div>;
    case 'contracts':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2348">
          <Select label="Tipo" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Contratto',
          label: 'Contratto'
        }, {
          value: 'Offerta',
          label: 'Offerta'
        }]} data-id="element-2349" />
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Attivo',
          label: 'Attivo'
        }, {
          value: 'In attesa',
          label: 'In attesa'
        }, {
          value: 'Bozza',
          label: 'Bozza'
        }, {
          value: 'Scaduto',
          label: 'Scaduto'
        }]} data-id="element-2350" />
        </div>;
    case 'invoices':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2351">
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Pagata',
          label: 'Pagata'
        }, {
          value: 'In attesa',
          label: 'In attesa'
        }, {
          value: 'Bozza',
          label: 'Bozza'
        }, {
          value: 'Scaduta',
          label: 'Scaduta'
        }]} data-id="element-2352" />
        </div>;
    case 'training':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2353">
          <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Sicurezza',
          label: 'Sicurezza'
        }, {
          value: 'HACCP',
          label: 'HACCP'
        }, {
          value: 'Privacy',
          label: 'Privacy'
        }]} data-id="element-2354" />
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'In corso',
          label: 'In corso'
        }, {
          value: 'Programmato',
          label: 'Programmato'
        }, {
          value: 'Completato',
          label: 'Completato'
        }]} data-id="element-2355" />
          <Select label="Modalità" value={filters.mode || ''} onChange={e => setFilter('mode', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Sede SEF',
          label: 'Sede SEF'
        }, {
          value: 'Sede cliente',
          label: 'Sede cliente'
        }, {
          value: 'A distanza',
          label: 'A distanza'
        }]} data-id="element-2356" />
        </div>;
    case 'operations':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2357">
          <Select label="Ambito" value={filters.ambito || ''} onChange={e => setFilter('ambito', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Sicurezza',
          label: 'Sicurezza'
        }, {
          value: 'Privacy',
          label: 'Privacy'
        }, {
          value: 'HACCP',
          label: 'HACCP'
        }, {
          value: 'Ambiente',
          label: 'Ambiente'
        }]} data-id="element-2358" />
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'da_pianificare',
          label: 'Da pianificare'
        }, {
          value: 'pianificato',
          label: 'Pianificata'
        }, {
          value: 'calendarizzato',
          label: 'Calendarizzata'
        }, {
          value: 'in_svolgimento_attivo',
          label: 'Attiva'
        }, {
          value: 'in_svolgimento_sospeso',
          label: 'Sospesa'
        }, {
          value: 'completo',
          label: 'Completa'
        }]} data-id="element-2359" />
          <Select label="Operatore" value={filters.operator || ''} onChange={e => setFilter('operator', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Alessio',
          label: 'Alessio'
        }, {
          value: 'Lorenzo',
          label: 'Lorenzo'
        }, {
          value: 'Gian Franco',
          label: 'Gian Franco'
        }, {
          value: 'Tiziano',
          label: 'Tiziano'
        }]} data-id="element-2360" />
        </div>;
    case 'non_conformities':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2361">
          <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Non conformità maggiore',
          label: 'NC Maggiore'
        }, {
          value: 'Non conformità minore',
          label: 'NC Minore'
        }, {
          value: 'Osservazione',
          label: 'Osservazione'
        }]} data-id="element-2362" />
          <Select label="Origine" value={filters.origin || ''} onChange={e => setFilter('origin', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Interna',
          label: 'Interna'
        }, {
          value: 'Cliente (reclamo)',
          label: 'Cliente (reclamo)'
        }, {
          value: 'Fornitore',
          label: 'Fornitore'
        }]} data-id="element-2363" />
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Aperta',
          label: 'Aperta'
        }, {
          value: 'Sospesa',
          label: 'Sospesa'
        }, {
          value: 'Chiusa',
          label: 'Chiusa'
        }]} data-id="element-2364" />
        </div>;
    case 'improvements':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2365">
          <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Approvato',
          label: 'Approvato'
        }, {
          value: 'In valutazione',
          label: 'In valutazione'
        }, {
          value: 'Sospesa',
          label: 'Sospesa'
        }, {
          value: 'Scartata',
          label: 'Scartata'
        }]} data-id="element-2366" />
          <Select label="Responsabile" value={filters.responsible || ''} onChange={e => setFilter('responsible', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Alessio',
          label: 'Alessio'
        }, {
          value: 'Lorenzo',
          label: 'Lorenzo'
        }, {
          value: 'Gian Franco',
          label: 'Gian Franco'
        }, {
          value: 'Tiziano',
          label: 'Tiziano'
        }]} data-id="element-2367" />
        </div>;
    case 'equipment':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2368">
          <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutte'
        }, {
          value: 'Strumento Misura',
          label: 'Strumento Misura'
        }, {
          value: 'Hardware',
          label: 'Hardware'
        }, {
          value: 'Veicolo',
          label: 'Veicolo'
        }, {
          value: 'Attrezzatura',
          label: 'Attrezzatura'
        }]} data-id="element-2369" />
          <Select label="Tipo manutenzione" value={filters.maintenanceType || ''} onChange={e => setFilter('maintenanceType', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Ordinaria',
          label: 'Ordinaria'
        }, {
          value: 'Straordinaria',
          label: 'Straordinaria'
        }]} data-id="element-2370" />
        </div>;
    case 'maintenance':
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2371">
          <Select label="Tipo" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Ordinaria',
          label: 'Ordinaria'
        }, {
          value: 'Straordinaria',
          label: 'Straordinaria'
        }]} data-id="element-2372" />
          <Select label="Esito" value={filters.result || ''} onChange={e => setFilter('result', e.target.value)} options={[{
          value: '',
          label: 'Tutti'
        }, {
          value: 'Positivo',
          label: 'Positivo'
        }, {
          value: 'Negativo',
          label: 'Negativo'
        }]} data-id="element-2373" />
        </div>;
    default:
      return null;
  }
}